import { request } from "./client";
import type {
  AlertEvent,
  AuthResponse,
  BlinkStatus,
  ButtonWithKey,
  Camera,
  Clip,
  Drone,
  DroneKind,
  DroneWithKey,
  Health,
  LiveSession,
  MeResponse,
  OkResponse,
  PanicButton,
  PushConfig,
  PushTestResult,
  Role,
  Site,
  User,
  UserWithTempPassword,
  WebPushRegistration,
} from "./types";

const enc = encodeURIComponent;

export const api = {
  // 1. Accounts (one organization, owner-managed; no public sign-up)
  login: (email: string, password: string) =>
    request<AuthResponse>("POST", "/api/auth/login", { body: { email, password }, auth: false }),
  /** Returns a NEW token; the old one is dead. A 401 here means the current password was wrong, not a dead session. */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<AuthResponse>("POST", "/api/auth/password", {
      body: { current_password: currentPassword, new_password: newPassword },
      ignore401: true,
    }),
  me: (signal?: AbortSignal) => request<MeResponse>("GET", "/api/me", { signal }),
  updateSite: (patch: { name?: string; motion_push?: boolean }) => request<Site>("PATCH", "/api/site", { body: patch }),
  users: (signal?: AbortSignal) => request<User[]>("GET", "/api/users", { signal }),
  createUser: (body: { email: string; name: string; role: Role }) => request<UserWithTempPassword>("POST", "/api/users", { body }),
  updateUser: (id: string, patch: { name?: string; role?: Role }) => request<User>("PATCH", `/api/users/${enc(id)}`, { body: patch }),
  resetUserPassword: (id: string) => request<UserWithTempPassword>("POST", `/api/users/${enc(id)}/reset-password`),
  deleteUser: (id: string) => request<OkResponse>("DELETE", `/api/users/${enc(id)}`),

  // 2. Blink
  blinkStatus: (signal?: AbortSignal) => request<BlinkStatus>("GET", "/api/blink/status", { signal }),
  blinkConnect: (email: string, password: string) =>
    request<BlinkStatus>("POST", "/api/blink/connect", { body: { email, password } }),
  blinkVerify: (code: string) => request<BlinkStatus>("POST", "/api/blink/verify", { body: { code } }),
  blinkDisconnect: () => request<BlinkStatus>("DELETE", "/api/blink"),
  cameras: (signal?: AbortSignal) => request<Camera[]>("GET", "/api/cameras", { signal }),
  snapshot: (cameraId: string) => request<OkResponse>("POST", `/api/cameras/${enc(cameraId)}/snapshot`),
  startLive: (cameraId: string) => request<LiveSession>("POST", `/api/cameras/${enc(cameraId)}/live`),
  stopLive: (cameraId: string) =>
    request<OkResponse>("DELETE", `/api/cameras/${enc(cameraId)}/live`, { keepalive: true }),
  clips: (opts: { cameraId?: string; limit?: number } = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ limit: String(opts.limit ?? 30) });
    if (opts.cameraId) qs.set("camera_id", opts.cameraId);
    return request<Clip[]>("GET", `/api/clips?${qs.toString()}`, { signal });
  },
  armNetwork: (networkId: string, armed: boolean) =>
    request<OkResponse>("POST", `/api/networks/${enc(networkId)}/arm`, { body: { armed } }),

  // 3. Events
  events: (opts: { limit?: number; before?: string } = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ limit: String(opts.limit ?? 50) });
    if (opts.before) qs.set("before", opts.before);
    return request<AlertEvent[]>("GET", `/api/events?${qs.toString()}`, { signal });
  },
  raiseAlert: (body: { title?: string; body?: string }) => request<AlertEvent>("POST", "/api/events", { body }),
  ackEvent: (id: string) => request<AlertEvent>("POST", `/api/events/${enc(id)}/ack`),

  // 4. Push
  registerDevice: (body: WebPushRegistration) => request<{ id: string }>("POST", "/api/devices", { body }),
  deleteDevice: (id: string) => request<unknown>("DELETE", `/api/devices/${enc(id)}`, { keepalive: true }),
  pushConfig: (signal?: AbortSignal) => request<PushConfig>("GET", "/api/push/config", { signal }),
  pushTest: () => request<PushTestResult>("POST", "/api/push/test"),

  // 5. Buttons
  buttons: (signal?: AbortSignal) => request<PanicButton[]>("GET", "/api/buttons", { signal }),
  addButton: (name: string, location: string | null) =>
    request<ButtonWithKey>("POST", "/api/buttons", { body: { name, location } }),
  updateButton: (id: string, patch: { name?: string; location?: string | null }) =>
    request<PanicButton>("PATCH", `/api/buttons/${enc(id)}`, { body: patch }),
  deleteButton: (id: string) => request<unknown>("DELETE", `/api/buttons/${enc(id)}`),
  rotateButtonKey: (id: string) => request<ButtonWithKey>("POST", `/api/buttons/${enc(id)}/rotate-key`),
  simulatePress: (id: string) => request<AlertEvent>("POST", `/api/buttons/${enc(id)}/simulate`),

  // 6. Drones
  drones: (signal?: AbortSignal) => request<Drone[]>("GET", "/api/drones", { signal }),
  addDrone: (name: string, kind: DroneKind) => request<DroneWithKey>("POST", "/api/drones", { body: { name, kind } }),
  deleteDrone: (id: string) => request<unknown>("DELETE", `/api/drones/${enc(id)}`),
  rotateDroneKey: (id: string) => request<DroneWithKey>("POST", `/api/drones/${enc(id)}/rotate-key`),

  // 8. Health
  health: (signal?: AbortSignal) => request<Health>("GET", "/health", { signal, auth: false }),
};
